// Achievement checker utility for CommandLearn
import achievements, { getAchievementById } from '../data/achievements';

/**
 * Check which achievements have been newly unlocked
 * @param {object} currentState - Current game state
 * @param {string[]} unlockedAchievements - Array of already unlocked achievement IDs
 * @returns {Array} Array of newly unlocked achievement objects
 */
export const checkAchievements = (currentState, unlockedAchievements = []) => {
  const newlyUnlocked = [];

  for (const achievement of achievements) {
    // Skip if already unlocked
    if (unlockedAchievements.includes(achievement.id)) {
      continue;
    }

    // Check if condition is met
    try {
      if (achievement.condition(currentState)) {
        newlyUnlocked.push(achievement);
      }
    } catch (error) {
      console.error(`Error checking achievement ${achievement.id}:`, error);
    }
  }

  return newlyUnlocked;
};

/**
 * Get progress towards an achievement
 * @param {string} achievementId - Achievement ID
 * @param {object} state - Current game state
 * @returns {object} { current, required, percentage, isComplete }
 */
export const getAchievementProgress = (achievementId, state) => {
  const achievement = getAchievementById(achievementId);
  if (!achievement) {
    return null;
  }

  // Define progress tracking for specific achievements
  const progressTrackers = {
    // Milestone achievements
    first_blood: { current: getTotalCompletedLessons(state), required: 1 },
    getting_started: { current: getTotalCompletedLessons(state), required: 10 },
    dedicated_learner: { current: getTotalCompletedLessons(state), required: 50 },
    centurion: { current: getTotalCompletedLessons(state), required: 100 },

    // Streak achievements
    streak_5: { current: state.bestStreak, required: 5 },
    streak_10: { current: state.bestStreak, required: 10 },
    streak_25: { current: state.bestStreak, required: 25 },
    streak_50: { current: state.bestStreak, required: 50 },

    // Speed achievements
    quick_draw: { current: state.fastAnswers, required: 5 },
    speed_demon: { current: state.fastAnswers, required: 25 },
    lightning_reflexes: { current: state.fastAnswers, required: 100 },

    // Skill achievements
    no_hints_10: { current: state.lessonsWithoutHints, required: 10 },
    no_hints_50: { current: state.lessonsWithoutHints, required: 50 },
    no_hints_100: { current: state.lessonsWithoutHints, required: 100 },

    // Topic mastery
    topic_master_bash: { current: state.completedLessons?.bash?.length || 0, required: 26 },
    topic_master_linux: { current: state.completedLessons?.linux?.length || 0, required: 20 },
    topic_master_git: { current: state.completedLessons?.git?.length || 0, required: 20 },
    topic_master_terraform: { current: state.completedLessons?.terraform?.length || 0, required: 20 },
    topic_master_ansible: { current: state.completedLessons?.ansible?.length || 0, required: 20 },
    topic_master_docker: { current: state.completedLessons?.docker?.length || 0, required: 30 },
    topic_master_networking: { current: state.completedLessons?.networking?.length || 0, required: 30 },
    topic_master_vim: { current: state.completedLessons?.vim?.length || 0, required: 30 },

    // Boss achievements
    boss_first: { current: state.bossesDefeated, required: 1 },
    boss_slayer: { current: state.bossesDefeated, required: 5 },
    boss_destroyer: { current: state.bossesDefeated, required: 10 },

    // Daily achievements
    daily_first: { current: state.dailyStreak, required: 1 },
    daily_warrior: { current: state.dailyStreak, required: 7 },
    daily_champion: { current: state.dailyStreak, required: 30 },

    // Level achievements
    level_apprentice: { current: state.totalXP, required: 100 },
    level_expert: { current: state.totalXP, required: 1000 },
    level_master: { current: state.totalXP, required: 1500 },
    level_legend: { current: state.totalXP, required: 3000 },

    // XP achievements
    xp_1000: { current: state.totalXP, required: 1000 },
    xp_5000: { current: state.totalXP, required: 5000 },
    xp_10000: { current: state.totalXP, required: 10000 }
  };

  const tracker = progressTrackers[achievementId];
  if (!tracker) {
    // For achievements without explicit tracking, just check if complete
    const isComplete = achievement.condition(state);
    return {
      current: isComplete ? 1 : 0,
      required: 1,
      percentage: isComplete ? 100 : 0,
      isComplete
    };
  }

  const percentage = Math.min(100, Math.round((tracker.current / tracker.required) * 100));
  const isComplete = tracker.current >= tracker.required;

  return {
    current: Math.min(tracker.current, tracker.required),
    required: tracker.required,
    percentage,
    isComplete
  };
};

/**
 * Get all achievements with their progress
 * @param {object} state - Current game state
 * @param {string[]} unlockedAchievements - Array of unlocked achievement IDs
 * @returns {Array} Achievements with progress info
 */
export const getAllAchievementsWithProgress = (state, unlockedAchievements = []) => {
  return achievements.map(achievement => ({
    ...achievement,
    isUnlocked: unlockedAchievements.includes(achievement.id),
    progress: getAchievementProgress(achievement.id, state)
  }));
};

/**
 * Get count of unlocked achievements
 * @param {string[]} unlockedAchievements - Array of unlocked achievement IDs
 * @returns {object} { unlocked, total, percentage }
 */
export const getAchievementStats = (unlockedAchievements = []) => {
  const total = achievements.length;
  const unlocked = unlockedAchievements.length;
  return {
    unlocked,
    total,
    percentage: Math.round((unlocked / total) * 100)
  };
};

// Helper function to count total completed lessons
function getTotalCompletedLessons(state) {
  return Object.values(state.completedLessons || {}).reduce(
    (total, lessons) => total + (lessons?.length || 0),
    0
  );
}

export default {
  checkAchievements,
  getAchievementProgress,
  getAllAchievementsWithProgress,
  getAchievementStats
};
